import type { EventRoom, GameEvent, ServerFlag, WorldData } from '../types/world'

/*
 * Estado de un evento SEPARADO en tres niveles, cada uno con su propia fuente:
 * - Evento: solo 27_events.active_configured.
 * - Subservidor: 27_events.enabled_by_server (y 33_event_rooms.available_servers por sala).
 * - Sala: 33_event_rooms (active, entry_enabled, valid_level_range).
 * Una sala o un subservidor deshabilitado NO desactiva el evento completo.
 * Todo es configuración exportada: no confirma la versión desplegada hoy ni una prueba en el juego.
 */

const TRUE = /^(true|1|yes|on|si|sí|enabled)$/i

/** Acepta JSON, repr de Python ({'servermain1': True}), listas o "a:1, b:0". */
export function parseServerFlags(raw?: string): ServerFlag[] {
  if (!raw) return []
  const s = raw.trim()
  const fromValue = (v: unknown): ServerFlag[] => {
    if (Array.isArray(v)) {
      return v.flatMap((x) =>
        typeof x === 'string'
          ? [{ server: x, enabled: true }]
          : x && typeof x === 'object'
            ? fromValue(x)
            : [],
      )
    }
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>
      if (typeof o.server === 'string') return [{ server: o.server, enabled: o.enabled === undefined ? true : o.enabled === true || TRUE.test(String(o.enabled)) }]
      return Object.entries(o).map(([server, x]) => ({ server, enabled: x === true || TRUE.test(String(x)) }))
    }
    return []
  }
  for (const candidate of [s, s.replace(/'/g, '"').replace(/\bTrue\b/g, 'true').replace(/\bFalse\b/g, 'false').replace(/\bNone\b/g, 'null')]) {
    try {
      return fromValue(JSON.parse(candidate))
    } catch {
      /* siguiente formato */
    }
  }
  return s
    .split(/[,;|]/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => {
      const m = x.match(/^([^:=]+)\s*[:=]\s*(.+)$/)
      return m ? { server: m[1].trim(), enabled: TRUE.test(m[2].trim()) } : { server: x, enabled: true }
    })
}

export type EventConfigState = 'habilitado' | 'deshabilitado' | 'sin-dato'

/** Estado del EVENTO: únicamente 27_events.active_configured. */
export function eventConfigState(e: GameEvent): EventConfigState {
  return e.activeConfigured === true ? 'habilitado' : e.activeConfigured === false ? 'deshabilitado' : 'sin-dato'
}

export const eventConfigLabel: Record<EventConfigState, string> = {
  habilitado: 'Habilitado en la configuración',
  deshabilitado: 'Deshabilitado en la configuración',
  'sin-dato': 'Estado no documentado',
}

export type RoomState = 'activa' | 'deshabilitada' | 'rango-invalido' | 'entrada-cerrada'

export const roomStateLabel: Record<RoomState, string> = {
  activa: 'Activa',
  deshabilitada: 'Deshabilitada',
  'rango-invalido': 'Rango de nivel inválido',
  'entrada-cerrada': 'Entrada deshabilitada',
}

/** Estado de UNA sala (33_event_rooms). No afecta al estado del evento ni al de otras salas. */
export function roomState(r: EventRoom): RoomState {
  if (r.active === false) return 'deshabilitada'
  if (r.validLevelRange === false || (r.levelMin !== undefined && r.levelMax !== undefined && r.levelMin > r.levelMax)) return 'rango-invalido'
  if (r.entryEnabled === false) return 'entrada-cerrada'
  return 'activa'
}

export type EventStatusSummary = {
  event: EventConfigState
  servers: ServerFlag[]
  rooms: { room: EventRoom; state: RoomState }[]
}

export function eventStatusSummary(w: WorldData, e: GameEvent): EventStatusSummary {
  const rooms = (w.idx.roomsByEvent.get(e.id) ?? [])
    .slice()
    .sort((a, b) => (a.roomNumber ?? 0) - (b.roomNumber ?? 0))
    .map((room) => ({ room, state: roomState(room) }))
  return { event: eventConfigState(e), servers: e.serverFlags, rooms }
}

/** Texto compacto: «Salas 1–6 activas · Sala 7 deshabilitada». */
export function roomStatesText(rooms: EventStatusSummary['rooms']): string {
  const groups = new Map<RoomState, number[]>()
  rooms.forEach(({ room, state }) => {
    if (room.roomNumber === undefined) return
    groups.set(state, [...(groups.get(state) ?? []), room.roomNumber])
  })
  const order: RoomState[] = ['activa', 'deshabilitada', 'rango-invalido', 'entrada-cerrada']
  return order
    .filter((s) => groups.get(s)?.length)
    .map((s) => {
      const ns = groups.get(s)!
      const label = s === 'activa' ? (ns.length > 1 ? 'activas' : 'activa') : roomStateLabel[s].toLowerCase()
      const extra = s === 'rango-invalido' ? ` (${ns.map((n) => { const r = rooms.find((x) => x.room.roomNumber === n)!.room; return `${r.levelMin ?? '?'}–${r.levelMax ?? '?'}` }).join(', ')})` : ''
      return `${ns.length > 1 ? 'Salas' : 'Sala'} ${compactRange(ns)} ${label}${extra}`
    })
    .join(' · ')
}

function compactRange(ns: number[]): string {
  const s = Array.from(new Set(ns)).sort((a, b) => a - b)
  const parts: string[] = []
  for (let i = 0; i < s.length; i++) {
    let j = i
    while (j + 1 < s.length && s[j + 1] === s[j] + 1) j++
    parts.push(j > i + 1 ? `${s[i]}–${s[j]}` : j === i + 1 ? `${s[i]}, ${s[j]}` : `${s[i]}`)
    i = j
  }
  return parts.join(', ')
}

export function serversText(servers: ServerFlag[]): string {
  const on = servers.filter((s) => s.enabled).map((s) => s.server)
  const off = servers.filter((s) => !s.enabled).map((s) => s.server)
  return [on.length ? `habilitado en ${on.join(', ')}` : '', off.length ? `deshabilitado en ${off.join(', ')}` : ''].filter(Boolean).join(' · ')
}
